import { formatDate } from '../../../../shared/format-date.js';
import { buildDateClause, isDateClause, parseDateClause } from '../../../../shared-web/utilities/filter-logic.js';

/**
 * Preset option values for date filters.
 * Keep in display order: This month, Last month, This year, From, To, Between
 * @type {ReadonlyArray<string>}
 */
export const DATE_PRESETS = Object.freeze(['thisMonth', 'lastMonth', 'thisYear', 'from', 'to', 'between']);

/**
 * Build Combobox options for date presets.
 * @param {(key: string, opts?: any) => string} t
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildDatePresetOptions(t) {
	return [
		{ value: 'thisMonth', label: t('filter.preset.thisMonth') },
		{ value: 'lastMonth', label: t('filter.preset.lastMonth') },
		{ value: 'thisYear', label: t('filter.preset.thisYear') },
		{ value: 'from', label: t('filter.preset.from') },
		{ value: 'to', label: t('filter.preset.to') },
		{ value: 'between', label: t('filter.preset.between') },
	];
}

/**
 * Parse stored clause value into UI state {preset, date, dateEnd}.
 * @param {string} value
 * @returns {{ preset: string, date: string, dateEnd: string }}
 */
export function parseDateClauseToState(value) {
	if (!value) { return { preset: '', date: '', dateEnd: '' }; }
	if (value === 'thisMonth' || value === 'lastMonth' || value === 'thisYear') {
		return { preset: value, date: '', dateEnd: '' };
	}
	const p = parseDateClause(value);
	if ((p.preset === 'from' || p.preset === 'to') && p.date) {
		return { preset: p.preset, date: p.date, dateEnd: '' };
	}
	if (p.preset === 'between' && p.date && p.dateEnd) {
		return { preset: 'between', date: p.date, dateEnd: p.dateEnd };
	}
	// legacy / unknown — treat as no preset
	return { preset: '', date: '', dateEnd: '' };
}

/**
 * Build stored clause value from UI preset+date(s).
 * @param {string} preset
 * @param {string} date
 * @param {string} [dateEnd]
 * @returns {string}
 */
export function buildDateClauseValue(preset, date, dateEnd) {
	if (!preset) { return ''; }
	if (preset === 'from' || preset === 'to') {
		if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { return ''; }
		return buildDateClause(preset, date);
	}
	if (preset === 'between') {
		if (!date || !dateEnd || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{4}-\d{2}-\d{2}$/.test(dateEnd)) { return ''; }
		if (date > dateEnd) { return ''; }
		return buildDateClause(preset, date, dateEnd);
	}
	if (['thisMonth', 'lastMonth', 'thisYear'].includes(preset)) { return preset; }
	return '';
}

/**
 * Whether a preset needs an additional date picker (count).
 * @param {string} preset
 * @returns {number}
 */
export function datePresetNeedsPicker(preset) {
	if (preset === 'between') { return 2; }
	if (preset === 'from' || preset === 'to') { return 1; }
	return 0;
}

/**
 * Whether a preset needs any picker (boolean).
 * @param {string} preset
 * @returns {boolean}
 */
export function datePresetNeedsAnyPicker(preset) {
	return datePresetNeedsPicker(preset) > 0;
}

/**
 * Format a stored date clause value for chip display.
 * @param {string} value
 * @param {(key: string, opts?: any) => string} t
 * @param {string} dateFormat
 * @returns {string}
 */
export function formatDateChipValue(value, t, dateFormat) {
	if (!isDateClause(value)) { return value; }
	if (value === 'thisMonth') { return t('filter.preset.thisMonth'); }
	if (value === 'lastMonth') { return t('filter.preset.lastMonth'); }
	if (value === 'thisYear') { return t('filter.preset.thisYear'); }
	if (value.startsWith('from:')) {
		const d = value.slice(5);
		const fmt = formatDate(d, dateFormat);
		const tmpl = t('filter.preset.fromDate', { date: fmt });
		if (tmpl && tmpl !== 'filter.preset.fromDate') { return tmpl; }
		return `From ${fmt}`;
	}
	if (value.startsWith('to:')) {
		const d = value.slice(3);
		const fmt = formatDate(d, dateFormat);
		const tmpl = t('filter.preset.toDate', { date: fmt });
		if (tmpl && tmpl !== 'filter.preset.toDate') { return tmpl; }
		return `To ${fmt}`;
	}
	if (value.startsWith('between:')) {
		const rest = value.slice(8);
		const parts = rest.split(':');
		const s = parts[0] ?? ''; const e = parts[1] ?? '';
		const sf = formatDate(s, dateFormat);
		const ef = formatDate(e, dateFormat);
		const tmpl = t('filter.preset.betweenDate', { start: sf, end: ef });
		if (tmpl && tmpl !== 'filter.preset.betweenDate') { return tmpl; }
		return `Between ${sf} – ${ef}`;
	}
	return value;
}
